<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold">
                                                Link Existing Supplier Invoice
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                Get started by filling in the information below to link an existing
                                                supplier invoice to this project.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="mt-6 grid grid-cols-2 gap-4">
                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Supplier Invoice Information</h3>
                                                </div>
                                                <div class="col-span-2">
                                                    <label for="supplier_invoice_id"
                                                           class="block text-sm text-gray-900 required">
                                                        Supplier Invoice
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="supplier_invoice_id" v-model="form.supplier_invoice_id"
                                                                :class="{'border-red-500': form.errors.supplier_invoice_id}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select a Supplier Invoice'"></option>
                                                            <option v-for="(invoice, index) in invoices"
                                                                    v-html="invoice.invoice_number + ' (£' + (number_format(parseFloat(invoice.amount).toFixed(2))) + ')'"
                                                                    :value="invoice.id"></option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="gbp_amount"
                                                           class="block text-sm text-gray-900 required">
                                                        GBP Amount
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="transaction_type_title" v-model="form.amount"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.amount}"
                                                        />
                                                    </div>
                                                </div>

                                                <div class="col-span-2 flex justify-end">
                                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-500">
                                                        <span v-if="loading === true">Saving..</span>
                                                        <span v-else>Link Supplier Invoice</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {useForm, usePage} from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import {emitter} from "@/eventHub";

export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        XIcon,
    },
    setup(prop, context) {

        const show = ref(false)

        const showErrors = ref(false)

        const loading = ref(null);

        const project = usePage().props.project;

        const invoices = ref([]);

        const form = useForm({
            id: null,
            project_id: "",
            supplier_invoice_id: "",
            amount: null
        })

        function submit() {
            this.form.post(route('frontend.project.supplier-invoice.invoice.related-project.store', [usePage().props.project.id, this.form.supplier_invoice_id]), {
                onSuccess: page => {
                    this.show = false;
                    this.showErrors = false;
                    this.form.reset();
                },
                onError: errors => {
                    this.showErrors = true;
                }
            });
        }

        function close() {
            this.form.reset();
            this.form.clearErrors();
            this.show = false;
        }

        function open(project) {
            this.loading = true;
            this.fetchOrganisationSupplierInvoices();

            this.loading = false;
            this.project = project;
            this.form.project_id = project.id;
            this.form.clearErrors();
            this.show = true;
        }

        const emitter = emitter;

        return {
            show,
            form,
            submit,
            close,
            open,
            showErrors,
            emitter,
            loading,
            project,
            invoices
        }
    },
    methods: {
        fetchOrganisationSupplierInvoices () {
            axios.get(route('frontend.project.get_link_existing_supplier_invoices_config', this.project.id)).then(response => {
                this.invoices = response.data.invoices;
            });
        },
        number_format(nStr) {
            nStr += '';
            let x = nStr.split('.');
            let x1 = x[0];
            let x2 = x.length > 1 ? '.' + x[1] : '';
            let rgx = /(\d+)(\d{3})/;
            while (rgx.test(x1)) {
                x1 = x1.replace(rgx, '$1' + ',' + '$2');
            }
            return x1 + x2;
        },
    },
    mounted() {
        emitter.on('openLinkProjectSupplierInvoiceSlideout', (project) => {
            this.open(project);
        });
    }
}
</script>
